import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Buildings, EnvelopeSimple, Lock, User, ArrowRight } from '@phosphor-icons/react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, name);
      navigate('/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1758193431393-0aead94a6a1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwbHV4dXJ5fGVufDB8fHx8MTc3NDgxMjkwM3ww&ixlib=rb-4.1.0&q=85"
          alt="Modern Building"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-heading text-3xl font-light leading-tight">
            Starten Sie heute.<br />
            <span className="font-black">Verwalten Sie morgen.</span>
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-8">
              <Buildings weight="bold" className="w-8 h-8 text-slate-900" />
              <span className="font-heading text-xl font-black tracking-tight">IMMO VERWALTER</span>
            </div>
            <h1 className="font-heading text-4xl font-black tracking-tight text-slate-900 mb-2">
              Konto erstellen
            </h1>
            <p className="text-slate-500 text-sm">
              Registrieren Sie sich kostenlos und starten Sie sofort
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                Name
              </label>
              <div className="relative">
                <User className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border-b border-slate-300 bg-transparent pl-8 pr-0 py-3 focus:border-slate-900 focus:ring-0 outline-none transition-colors text-slate-900"
                  placeholder="Max Mustermann"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                E-Mail
              </label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border-b border-slate-300 bg-transparent pl-8 pr-0 py-3 focus:border-slate-900 focus:ring-0 outline-none transition-colors text-slate-900"
                  placeholder="ihre@email.de"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                Passwort
              </label>
              <div className="relative">
                <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border-b border-slate-300 bg-transparent pl-8 pr-0 py-3 focus:border-slate-900 focus:ring-0 outline-none transition-colors text-slate-900"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white py-3 px-6 font-medium hover:bg-slate-800 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="register-submit-button"
            >
              {loading ? 'Wird erstellt...' : 'Registrieren'}
              <ArrowRight weight="bold" className="w-4 h-4" />
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-slate-500">
            Bereits registriert?{' '}
            <Link to="/login" className="text-slate-900 font-medium hover:underline" data-testid="login-link">
              Jetzt anmelden
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
